import React, { useState } from "react";
import PropTypes from "prop-types";
import * as inputTypes from "components/Forms/InputTypes";
import CogButton from "components/Forms/Buttons/AddField/Cog";
import Textbox from "components/Forms/Elements/Textbox";
import hasProperty from "lodash/has";
import Translate from "helpers/Translate";

function MultiSelectContainer({
  isFirstItem,
  isLastItem,
  inputs,
  type = inputTypes.SELECT,
  properties,
  errors = {},
  onMoveUpClick,
  onMoveDownClick,
  onTrashClick,
  onInputChange,
  onNewOptionClick,
  onOptionRemove,
  onSelectMenu
}) {
  const random = Math.random();
  const [renamingOrder, setRenamingOrder] = useState(
    properties.renaming_file_order || ""
  );

  const placeholder = type => {
    switch (type) {
      case inputTypes.SELECT:
        return Translate({ id: "document_type" });
      case inputTypes.MULTISELECT:
        return Translate({ id: "general_poll" });
      case inputTypes.RADIOBOX:
        return Translate({ id: "gender" });
      case inputTypes.CHECKBOX:
        return Translate({ id: "age_groups" });
      default:
        return "";
    }
  };

  return (
    <div className="box mb-20">
      <div className="title my-10">
        {Translate({ id: "input_type" })} :{" "}
        <span className="value">{type.toUpperCase()}</span>
        <div className="mini-toolbar fr">
          {!isFirstItem > 0 && (
            <i
              title={Translate({ id: "move_up" })}
              className="fas fa-caret-up move mr-10"
              onClick={() => onMoveUpClick(properties.uuid_container)}
            ></i>
          )}
          {!isLastItem && (
            <i
              title={Translate({ id: "move_down" })}
              className="fas fa-caret-down move mr-10"
              onClick={() => onMoveDownClick(properties.uuid_container)}
            ></i>
          )}
          <i
            title={Translate({ id: "remove" })}
            className="fas fa-trash-alt trash"
            onClick={() => onTrashClick(properties)}
          ></i>
        </div>
      </div>
      <div className="form-input-vgroup my-10">
        <label
          className="input-item"
          htmlFor={`mandatory-${properties.uuid_container}`}
        >
          {" "}
          <input
            type="checkbox"
            id={`mandatory-${properties.uuid_container}`}
            className="form-input"
            checked={properties.mandatory}
            onChange={e =>
              onInputChange({
                uuid_container: properties.uuid_container,
                mandatory: e.target.checked
              })
            }
          />{" "}
          {Translate({ id: "mandatory" })}
        </label>
      </div>
      {/* Rename Checkbox */}
      <div className="form-input-vgroup my-10">
        <label
          htmlFor={`rename-${properties.uuid_container}`}
          className="input-item"
        >
          <input
            type="checkbox"
            id={`rename-${properties.uuid_container}`}
            className="form-input"
            checked={properties.rename || false}
            onChange={e =>
              onInputChange({
                uuid_container: properties.uuid_container,
                rename: e.target.checked
              })
            }
          />

          {Translate({ id: "use_in_renaming_file" })}
        </label>
        {properties.rename && (
          <select
            id={`rename-order${properties.uuid_container}`}
            className="form-input mr-20"
            value={renamingOrder}
            onChange={e => {
              setRenamingOrder(e.target.value);
              onInputChange({
                uuid_container: properties.uuid_container,
                renaming_file_order: e.target.value
              });
            }}
          >
            <option>Select Renaming Order</option>
            {inputs.map((inp, index) => (
              <option key={index}>{index + 1}</option>
            ))}
          </select>
        )}
      </div>
      {/* Display option to render in Vertical or Horizontal */}
      {[inputTypes.RADIOBOX, inputTypes.CHECKBOX].includes(type) && (
        <div className="form-input-hgroup my-10">
          <label className="input-item">
            <input
              type="radio"
              name={`direction-${random}-${properties.uuid_container}`}
              className="form-input"
              id={`direction-${properties.uuid_container}`}
              checked={properties.direction === inputTypes.HORIZONTAL}
              value={inputTypes.HORIZONTAL}
              onChange={e =>
                onInputChange({
                  uuid_container: properties.uuid_container,
                  direction: e.target.value
                })
              }
            />
            {Translate({ id: "horizontal" })}
          </label>
          <label className="input-item">
            <input
              type="radio"
              name={`direction-${random}-${properties.uuid_container}`}
              className="form-input"
              id={`direction-${properties.uuid_container}`}
              checked={properties.direction === inputTypes.VERTICAL}
              value={inputTypes.VERTICAL}
              onChange={e =>
                onInputChange({
                  uuid_container: properties.uuid_container,
                  direction: e.target.value
                })
              }
            />
            {Translate({ id: "vertical" })}
          </label>
        </div>
      )}

      <div className="mb-10">
        <label htmlFor={`name-${properties.uuid_container}`}>
          {Translate({ id: "unique_field_name" })}
        </label>
        <Textbox
          type="text"
          id={`name-${properties.uuid_container}`}
          name="name"
          placeholder={placeholder(type)}
          fieldValue={properties.name}
          errorKey={`fields.${properties.key}.name`}
          onReturnKey={() => {}}
          errors={errors}
          formErrorClass="form-error-inverse"
          onChange={e =>
            onInputChange({
              uuid_container: properties.uuid_container,
              name: e.target.value
            })
          }
          onBlur={e =>
            onInputChange({
              uuid_container: properties.uuid_container,
              name: e.target.value.replace(/\s/g, "_")
            })
          }
        />

        <div className="help mt-5">
          {Translate({ id: "name_that_uniquely_identifies_this_field" })}
        </div>
      </div>

      <div className="mb-10">
        <label htmlFor={`label-${properties.uuid_container}`}>
          {Translate({ id: "label_text" })}{" "}
        </label>
        <Textbox
          type="text"
          id={`label-${properties.uuid_container}`}
          name="label"
          errorKey={`fields.${properties.key}.label`}
          fieldValue={properties.label}
          onReturnKey={() => {}}
          errors={errors}
          formErrorClass="form-error-inverse"
          onChange={e =>
            onInputChange({
              uuid_container: properties.uuid_container,
              label: e.target.value
            })
          }
        />
      </div>

      {properties.options &&
        properties.options.map((option, idx) => (
          <div className="ml-10 mb-10" key={idx}>
            <label className="ml-200">{Translate({ id: "value" })} </label>
            <div className="form-text-hgroup">
              <div className="input-item">
                <input
                  type={
                    [inputTypes.RADIOBOX, inputTypes.SELECT].includes(type)
                      ? "radio"
                      : "checkbox"
                  }
                  name={`default-${option.uuid_child}`}
                  title={Translate({ id: "set_as_default" })}
                  className="mr-100 hide"
                  id={`default-${option.uuid_child}`}
                  checked={
                    false &&
                    properties.uuid_selected_options &&
                    properties.uuid_selected_options.includes(option.uuid_child)
                  }
                  onChange={e => {
                    onInputChange({
                      uuid_container: properties.uuid_container,
                      uuid_child: option.uuid_child,
                      type,
                      isChecked: e.target.checked,
                      hasChildOptions: true,
                      isChildTextbox: false,
                      defaultSelectedValues: [option.value]
                    });
                  }}
                />
                <div>
                  <input
                    type="text"
                    className="form-input-sm mr-10"
                    id={`value-${option.uuid_child}`}
                    value={option.value}
                    onChange={e =>
                      onInputChange({
                        uuid_container: properties.uuid_container,
                        uuid_child: option.uuid_child,
                        type,
                        hasChildOptions: true,
                        isChildTextbox: true,
                        value: e.target.value
                      })
                    }
                  />
                  {hasProperty(errors, "response.data.data.errors") &&
                    errors.response.data.data.errors.length > 0 &&
                    option.value === "" &&
                    errors.response.data.data.errors[0].field.match(
                      /^fields\..*\.options$/i
                    ) && (
                      <div key={idx} className="form-error-inverse">
                        {Translate({ id: "this_field_is_required" })}
                      </div>
                    )}
                </div>
                {properties.options.length > 1 && (
                  <i
                    className="fas fa-minus-square pointer mr-5"
                    title={Translate({ id: "remove" })}
                    onClick={() =>
                      onOptionRemove({
                        uuid_container: properties.uuid_container,
                        uuid_child: option.uuid_child
                      })
                    }
                  ></i>
                )}
                <div className="add-child">
                  <CogButton
                    onSelectMenu={inputType =>
                      onSelectMenu({ inputType, parent: option })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        ))}

      <div className="my-30 addmore">
        <i
          className="fas fa-plus-square fr"
          onClick={() => onNewOptionClick(properties.uuid_container)}
        ></i>
      </div>
    </div>
  );
}

MultiSelectContainer.propTypes = {
  isFirstItem: PropTypes.bool.isRequired,
  isLastItem: PropTypes.bool.isRequired,
  type: PropTypes.string.isRequired,
  properties: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onTrashClick: PropTypes.func.isRequired,
  onMoveUpClick: PropTypes.func.isRequired,
  onMoveDownClick: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onNewOptionClick: PropTypes.func.isRequired,
  onOptionRemove: PropTypes.func.isRequired,
  onSelectMenu: PropTypes.func
};

export default MultiSelectContainer;
